'use client';

/**
 * ValidationResults Component
 *
 * Displays validation errors and warnings from import validation.
 * Groups issues by type and provides expandable details.
 */

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import { alpha, styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AnimatePresence, motion } from 'framer-motion';
import type React from 'react';
import { useState } from 'react';
import { getNeonStyles, type NeonAccent } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import { transitionPresets } from '../../theme/transitions';
import { iconSizes } from '../../theme/typography';
import type {
  ImportValidationError,
  ImportValidationResult,
  ImportValidationWarning,
} from '../../types/entityFramework';

// ============================================================================
// Types
// ============================================================================

export interface ValidationResultsProps {
  /**
   * Validation result from the import validation
   */
  validationResult: ImportValidationResult;

  /**
   * Maximum number of issues to show initially
   * @default 5
   */
  maxVisibleIssues?: number;

  /**
   * Neon accent color
   * @default "cyan"
   */
  neonAccent?: NeonAccent;

  /**
   * Additional class name
   */
  className?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

const StyledSummaryCard = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{
  $isDark: boolean;
  $status: 'success' | 'warning' | 'error';
}>(({ theme, $isDark, $status }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  const statusColors = {
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
  };

  return {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: 16,
    borderRadius: 12,
    backgroundColor: alpha(statusColors[$status], $isDark ? 0.12 : 0.08),
    border: `1px solid ${alpha(statusColors[$status], 0.3)}`,
    backdropFilter: glass.backdropBlur,
    marginBottom: 16,
  };
});

const StyledStatsGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
  gap: 16,
  marginBottom: 24,
});

const StyledStatCard = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{
  $isDark: boolean;
  $neonAccent: NeonAccent;
}>(({ $isDark, $neonAccent }) => {
  const neonStyles = getNeonStyles($neonAccent, $isDark);
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    backgroundColor: glass.background,
    border: `1px solid ${neonStyles.border}`,
    textAlign: 'center',
  };
});

const StyledIssueSection = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isDark: boolean }>(({ $isDark }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    borderRadius: 12,
    backgroundColor: glass.background,
    border: `1px solid ${glass.border}`,
    overflow: 'hidden',
    marginBottom: 16,
  };
});

const StyledIssueSectionHeader = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{
  $isDark: boolean;
  $type: 'error' | 'warning';
}>(({ theme, $isDark, $type }) => {
  const color = $type === 'error' ? theme.palette.error.main : theme.palette.warning.main;

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    backgroundColor: alpha(color, $isDark ? 0.1 : 0.06),
    borderBottom: `1px solid ${alpha(color, 0.2)}`,
    cursor: 'pointer',
    transition: transitionPresets.allFast,

    '&:hover': {
      backgroundColor: alpha(color, $isDark ? 0.15 : 0.1),
    },
  };
});

const StyledIssueRow = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{
  $isDark: boolean;
  $isOdd: boolean;
}>(({ $isDark, $isOdd }) => ({
  display: 'grid',
  gridTemplateColumns: '80px 120px 1fr auto',
  gap: 12,
  padding: '10px 16px',
  alignItems: 'center',
  backgroundColor: $isOdd ? ($isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)') : 'transparent',
  borderBottom: `1px solid ${$isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,

  '&:last-child': {
    borderBottom: 'none',
  },
}));

const StyledShowMoreButton = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isDark: boolean }>(({ $isDark }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px 16px',
  color: $isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)',
  cursor: 'pointer',
  transition: transitionPresets.allFast,

  '&:hover': {
    backgroundColor: $isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
    color: $isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.7)',
  },
}));

const StyledValueChip = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isDark: boolean }>(({ $isDark }) => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 4,
  backgroundColor: $isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
  fontFamily: 'monospace',
  fontSize: '0.75rem',
  maxWidth: 150,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

// ============================================================================
// Component
// ============================================================================

export const ValidationResults: React.FC<ValidationResultsProps> = ({
  validationResult,
  maxVisibleIssues = 5,
  neonAccent = 'cyan',
  className,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const neonStyles = getNeonStyles(neonAccent, isDark);

  const [errorsExpanded, setErrorsExpanded] = useState(true);
  const [warningsExpanded, setWarningsExpanded] = useState(true);
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [showAllWarnings, setShowAllWarnings] = useState(false);

  const { isValid, totalRows, validRows, invalidRows, errors, warnings } = validationResult;

  const overallStatus = isValid ? (warnings.length > 0 ? 'warning' : 'success') : 'error';

  const visibleErrors = showAllErrors ? errors : errors.slice(0, maxVisibleIssues);
  const visibleWarnings = showAllWarnings ? warnings : warnings.slice(0, maxVisibleIssues);

  const renderIssueRow = (
    issue: ImportValidationError | ImportValidationWarning,
    index: number,
    type: 'error' | 'warning',
  ) => {
    const isError = type === 'error';
    const errorIssue = issue as ImportValidationError;

    return (
      <StyledIssueRow
        key={`${type}-${issue.row}-${issue.field}-${index}`}
        $isDark={isDark}
        $isOdd={index % 2 === 1}
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.02 }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          Row {issue.row}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
          {issue.field}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          {issue.message}
        </Typography>
        {isError && errorIssue.value !== undefined && (
          <StyledValueChip $isDark={isDark}>{String(errorIssue.value)}</StyledValueChip>
        )}
      </StyledIssueRow>
    );
  };

  return (
    <Box className={className}>
      {/* Summary Card */}
      <StyledSummaryCard $isDark={isDark} $status={overallStatus}>
        {overallStatus === 'success' ? (
          <CheckCircleIcon sx={{ fontSize: iconSizes['3xl'], color: theme.palette.success.main }} />
        ) : overallStatus === 'warning' ? (
          <WarningIcon sx={{ fontSize: iconSizes['3xl'], color: theme.palette.warning.main }} />
        ) : (
          <ErrorIcon sx={{ fontSize: iconSizes['3xl'], color: theme.palette.error.main }} />
        )}

        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            {overallStatus === 'success'
              ? 'Validation Successful'
              : overallStatus === 'warning'
                ? 'Validation Passed with Warnings'
                : 'Validation Failed'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {overallStatus === 'success'
              ? 'All rows are valid and ready for import.'
              : overallStatus === 'warning'
                ? `${validRows} rows are valid. Please review the warnings below.`
                : `${invalidRows} rows have errors that need to be fixed.`}
          </Typography>
        </Box>
      </StyledSummaryCard>
      {/* Statistics Grid */}
      <StyledStatsGrid>
        <StyledStatCard $isDark={isDark} $neonAccent={neonAccent}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: neonStyles.base }}>
            {totalRows}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            Total Rows
          </Typography>
        </StyledStatCard>

        <StyledStatCard $isDark={isDark} $neonAccent={neonAccent}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
            {validRows}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            Valid Rows
          </Typography>
        </StyledStatCard>

        <StyledStatCard $isDark={isDark} $neonAccent={neonAccent}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.error.main }}>
            {invalidRows}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            Invalid Rows
          </Typography>
        </StyledStatCard>

        <StyledStatCard $isDark={isDark} $neonAccent={neonAccent}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.warning.main }}>
            {warnings.length}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            Warnings
          </Typography>
        </StyledStatCard>
      </StyledStatsGrid>
      {/* Errors Section */}
      {errors.length > 0 && (
        <StyledIssueSection $isDark={isDark}>
          <StyledIssueSectionHeader $isDark={isDark} $type="error" onClick={() => setErrorsExpanded(!errorsExpanded)}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ErrorIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Errors ({errors.length})
              </Typography>
            </Box>
            <IconButton size="small">{errorsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
          </StyledIssueSectionHeader>

          <Collapse in={errorsExpanded}>
            <Box>
              {/* Column Headers */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '80px 120px 1fr auto',
                  gap: 12,
                  padding: '8px 16px',
                  backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                  borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Row
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Field
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Message
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Value
                </Typography>
              </Box>

              <AnimatePresence>
                {visibleErrors.map((error, index) => renderIssueRow(error, index, 'error'))}
              </AnimatePresence>

              {errors.length > maxVisibleIssues && (
                <StyledShowMoreButton $isDark={isDark} onClick={() => setShowAllErrors(!showAllErrors)}>
                  <Typography variant="body2">
                    {showAllErrors ? 'Show Less' : `Show ${errors.length - maxVisibleIssues} More Errors`}
                  </Typography>
                  {showAllErrors ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </StyledShowMoreButton>
              )}
            </Box>
          </Collapse>
        </StyledIssueSection>
      )}
      {/* Warnings Section */}
      {warnings.length > 0 && (
        <StyledIssueSection $isDark={isDark}>
          <StyledIssueSectionHeader
            $isDark={isDark}
            $type="warning"
            onClick={() => setWarningsExpanded(!warningsExpanded)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Warnings ({warnings.length})
              </Typography>
            </Box>
            <IconButton size="small">{warningsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
          </StyledIssueSectionHeader>

          <Collapse in={warningsExpanded}>
            <Box>
              {/* Column Headers */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '80px 120px 1fr',
                  gap: 12,
                  padding: '8px 16px',
                  backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                  borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Row
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Field
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Message
                </Typography>
              </Box>

              <AnimatePresence>
                {visibleWarnings.map((warning, index) => renderIssueRow(warning, index, 'warning'))}
              </AnimatePresence>

              {warnings.length > maxVisibleIssues && (
                <StyledShowMoreButton $isDark={isDark} onClick={() => setShowAllWarnings(!showAllWarnings)}>
                  <Typography variant="body2">
                    {showAllWarnings ? 'Show Less' : `Show ${warnings.length - maxVisibleIssues} More Warnings`}
                  </Typography>
                  {showAllWarnings ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </StyledShowMoreButton>
              )}
            </Box>
          </Collapse>
        </StyledIssueSection>
      )}
      {/* No issues message */}
      {errors.length === 0 && warnings.length === 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 4,
          }}
        >
          <CheckCircleIcon
            sx={{
              fontSize: iconSizes['4xl'],
              color: theme.palette.success.main,
              mb: 2,
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            All Clear!
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            No errors or warnings found. Your data is ready for import.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

ValidationResults.displayName = 'ValidationResults';

export default ValidationResults;
